import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { setPageTitle } from '@fry-it/risr-core';
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { UserProfile } from "oidc-client-ts";
import { UserProfileService } from "@fry/organisation/user/user-profile/user-profile.service";
import { MatFormField } from "@angular/material/form-field";
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from "@angular/material/autocomplete";
import { AsyncPipe, CommonModule } from "@angular/common";
import { CredentialService } from "@fry/system/account/credential/crendential.service";
import { MatInput } from "@angular/material/input";
import { SecurityService } from '@fry-it/risr-core';
import { SYSTEM_ROLE_ID } from '@fry-it/risr-core';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'eas-pre-register-api-credential',
  templateUrl: './pre-register-api-credential.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    MatFormField,
    MatAutocomplete,
    MatOption,
    AsyncPipe,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatInput
  ],
})
export class PreRegisterApiCredentialComponent {
  accountId = ''; // Chosen user ID
  userSearchControl = new FormControl(); // Form control for the autocomplete
  filteredUsers$: Observable<UserProfile[]> | undefined; // Observable for user list

  private route = inject(ActivatedRoute);

  public url = toSignal(this.route.queryParamMap.pipe(map(params => params.get('url'))));
  public state = toSignal(this.route.queryParamMap.pipe(map(params => params.get('state'))));

  constructor(
    private credentialService: CredentialService,
    private userProfileService: UserProfileService,
    private snackBar: MatSnackBar,
    private securityService: SecurityService,
    private router: Router,
  ) {
    from(this.securityService.hasRole(SYSTEM_ROLE_ID.SUPER_ADMIN)).subscribe((isAdmin) => {
      if (!isAdmin) {
        this.router.navigate(['/']);
      }
    });

  }

  ngOnInit() {
    if (!this.url() || !this.state()) {
      this.snackBar.open('Required parameters "url" or "state" are missing.', 'Close', { duration: 3000 });
      this.router.navigate(['/']);
      return
    }

    setPageTitle('Pre register API credential');

    // User typeahead observable pipeline
    this.filteredUsers$ = this.userSearchControl.valueChanges.pipe(
      debounceTime(300), // Debounce user input to reduce API calls
      switchMap((searchText) =>
        from(this.userProfileService.search(searchText)).pipe(
          switchMap((searchResponse) => {
            const ids = searchResponse.data?.ids || []; // Extract IDs
            if (ids.length === 0) return of([]); // Return empty array if no IDs are found

            return from(this.userProfileService.fetch(ids)).pipe(
              map((userProfiles) => userProfiles || []) // Extract user details
            );
          }),
          catchError((error) => {
            console.error('User search or fetch error:', error);
            return of([]); // Return empty array on error
          })
        )
      )
    );
  }

  displayUser(userProfile: any): string {
    return userProfile ? `${userProfile.firstName} ${userProfile.lastName} (${userProfile.email})` : '';
  }

  onUserSelected(userProfile: any): void {
    // Assuming `userProfile.user` contains the UUID you need
    this.accountId = userProfile?.user; // Set the accountId to the UUID
  }

  onSubmit() {
    const accountId = this.accountId;

    if (!accountId) {
      this.snackBar.open('Please select a user.', 'Close', { duration: 3000 });
      return;
    }

    this.credentialService.preRegisterApiCredential(accountId)
      .then(response => {
        if (response.error) {
          this.snackBar.open('Failed to pre-register API credential. Reason: ' + response.error, 'Close', { duration: 3000 });
          return;
        }
        this.snackBar.open('API credential pre-registered successfully. Redirecting...', 'Close', { duration: 3000 });
        const state = this.state();
        if (!state) {
          this.snackBar.open("Missing state parameter, cannot redirect")
          return;
        }
        const queryParams = new URLSearchParams({state, ...response.data}).toString();
        window.open(`${this.url()}?${queryParams}`, '_blank');
      })
      .catch(error => {
        this.snackBar.open('Failed to pre-register API credential. Reason: ' + error, 'Close', { duration: 3000 });
      });
  }
}
